import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchProductsLite } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { MoleculePoster } from "./MoleculePoster";

const STORAGE_KEY = "hxr_recently_viewed_v1";
const MAX_ITEMS = 8;

/** Push a product id onto the recently-viewed stack (most recent first, deduped, capped). */
export function pushRecentlyViewed(productId) {
    if (!productId || typeof window === "undefined") return;
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const list = Array.isArray(raw) ? raw.filter((x) => x && x !== productId) : [];
        list.unshift(productId);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    } catch { /* ignore */ }
}

function readRecentlyViewed() {
    if (typeof window === "undefined") return [];
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        return Array.isArray(raw) ? raw : [];
    } catch { return []; }
}

/**
 * Recently Viewed rail — a horizontal scroll of the last N products the user opened.
 * Skips itself if there are <2 entries, or if the only recent entry is the product currently
 * being viewed (avoids showing "you viewed this product you're already on").
 *
 * Mounts on Home + Product Detail. Reads from localStorage (no auth, no API hit beyond /products).
 */
export const RecentlyViewedRail = ({ excludeId = null, title = "RECENTLY VIEWED", subtitle = "Pick up where you left off" }) => {
    const { formatPrice } = useApp();
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState(null);

    useEffect(() => {
        const ids = readRecentlyViewed().filter((id) => id !== excludeId);
        if (ids.length === 0) return;
        fetchProductsLite()
            .then((all) => {
                setProducts(all);
                // Filter to viewed ids, preserving order
                const map = new Map(all.map((p) => [p.id, p]));
                const list = ids.map((id) => map.get(id)).filter(Boolean).slice(0, 6);
                setItems(list);
            })
            .catch(() => { /* silent — rail just won't render */ });
    }, [excludeId]);

    if (!products || items.length < 2) return null;

    return (
        <section className="border-t border-ink bg-ink-base" data-testid="recently-viewed-rail">
            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-16">
                <div className="flex items-end justify-between gap-6 mb-8">
                    <div>
                        <p className="overline text-neon text-[10px] mb-1">/ {title}</p>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink leading-none">
                            {subtitle}
                        </h2>
                    </div>
                    <Link
                        to="/shop"
                        className="hidden sm:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-ink-dim hover:text-neon"
                        data-testid="recently-viewed-shop-link"
                    >
                        BROWSE FULL CATALOG <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {items.map((p) => {
                        const soldOut = p.in_stock === false;
                        return (
                            <Link
                                key={p.id}
                                to={`/product/${p.slug}`}
                                className={`group border border-ink bg-ink-surface flex flex-col relative overflow-hidden hover:border-neon/60 transition-colors ${soldOut ? "opacity-60" : ""}`}
                                data-testid={`recently-viewed-item-${p.slug}`}
                            >
                                <div className="relative aspect-square overflow-hidden bg-ink-elevated">
                                    {p.front_image ? (
                                        <img
                                            src={p.front_image}
                                            alt={p.name}
                                            loading="lazy"
                                            decoding="async"
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    ) : (
                                        <MoleculePoster molecule={p.molecule || {}} className="w-full h-full" />
                                    )}
                                    {soldOut && (
                                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 text-white overline text-[8px] font-bold">
                                            SOLD OUT
                                        </div>
                                    )}
                                </div>
                                <div className="p-3 flex flex-col gap-1 flex-1">
                                    <p className="overline text-[9px] text-ink-mute truncate">{p.category}</p>
                                    <p className="font-display text-sm font-bold text-ink leading-tight line-clamp-2">{p.name.replace(/\s*\(.*?\)\s*$/, "")}</p>
                                    <p className="text-[10px] font-mono text-ink-dim truncate">{p.variant_label || p.dosage}</p>
                                    <div className="mt-auto pt-2 flex items-center justify-between">
                                        <span className="font-display text-sm font-bold text-ink">{formatPrice(p.price_usd)}</span>
                                        <ArrowUpRight className="w-3.5 h-3.5 text-ink-mute group-hover:text-neon transition-colors" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
